import { useMemo } from "react";
import useAxios from "axios-hooks";
import {
  MessageBar,
  MessageBarType,
  Spinner,
  Stack,
  Text
} from "@fluentui/react";
import DetailsTable from "../../../Table/DetailsTable";

const formatUtcDate = (dateValue) => {
  if (!dateValue || dateValue === "0001-01-01T00:00:00") {
    return "N/A";
  }

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }

  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()} UTC`;
};

const CertificateAuditTab = ({ cobrands }) => {
  const [{ data: certificateStatusData, loading: loadingCertificateAudit, error: certificateAuditError }] = useAxios(
    "/internal/cobrand/cobrandidp/certificates/status"
  );

  const cobrandNameById = useMemo(
    () => new Map((cobrands || []).map((c) => [c.cobrandId, c.name])),
    [cobrands]
  );

  const expiringSoonCertificates = useMemo(() => {
    const expiringIn7Days = (certificateStatusData?.expiringIn7Days || []).map((certificate) => ({
      ...certificate,
      auditBucket: "Expiring in 7 days"
    }));

    const expiringIn30Days = (certificateStatusData?.expiringIn30Days || []).map((certificate) => ({
      ...certificate,
      auditBucket: "Expiring in 30 days"
    }));

    const expiringIn90Days = (certificateStatusData?.expiringIn90Days || []).map((certificate) => ({
      ...certificate,
      auditBucket: "Expiring in 90 days"
    }));

    return [...expiringIn7Days, ...expiringIn30Days, ...expiringIn90Days]
      .map((certificate) => ({
        ...certificate,
        cobrandName: cobrandNameById.get(certificate.cobrandId) || `CoBrand ${certificate.cobrandId}`
      }))
      .sort((a, b) => new Date(a.expirationDateUtc) - new Date(b.expirationDateUtc));
  }, [certificateStatusData, cobrandNameById]);

  const certificateAuditColumns = useMemo(
    () => [
      {
        key: "expirationDateUtc",
        name: "Expires (UTC)",
        fieldName: "expirationDateUtc",
        minWidth: 180,
        maxWidth: 220,
        isResizable: true,
        onRender: (item) => formatUtcDate(item.expirationDateUtc)
      },
      {
        key: "auditBucket",
        name: "Window",
        fieldName: "auditBucket",
        minWidth: 140,
        maxWidth: 170,
        isResizable: true
      },
      {
        key: "cobrandName",
        name: "CoBrand",
        fieldName: "cobrandName",
        minWidth: 160,
        maxWidth: 220,
        isResizable: true
      },
      {
        key: "entityId",
        name: "Entity Id",
        fieldName: "entityId",
        minWidth: 220,
        maxWidth: 320,
        isResizable: true
      },
      {
        key: "use",
        name: "Use",
        fieldName: "use",
        minWidth: 90,
        maxWidth: 110,
        isResizable: true
      },
      {
        key: "thumbprint",
        name: "Thumbprint",
        fieldName: "thumbprint",
        minWidth: 220,
        maxWidth: 320,
        isResizable: true
      },
      {
        key: "subject",
        name: "Subject",
        fieldName: "subject",
        minWidth: 260,
        maxWidth: 380,
        isResizable: true
      }
    ],
    []
  );

  return (
    <Stack tokens={{ childrenGap: 8 }}>
      <Text variant="small">
        Review SSO Certificates across all CoBrands to see if any will expire in the next 90 days. In some cases it may take two weeks for customers to generate and provide new certificates, so early communication is recommended.
      </Text>
      <Text variant="xSmall" styles={{ root: { color: "#605e5c" } }}>
        Data snapshot generated: {formatUtcDate(certificateStatusData?.generatedAtUtc)}
      </Text>

      {loadingCertificateAudit && <Spinner label="Loading certificate audit..." />}

      {certificateAuditError && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
          Unable to load certificate audit data. Please refresh and try again.
        </MessageBar>
      )}

      {!loadingCertificateAudit && !certificateAuditError && expiringSoonCertificates.length === 0 && (
        <MessageBar messageBarType={MessageBarType.success} isMultiline={false}>
          No certificates are scheduled to expire within the next 90 days.
        </MessageBar>
      )}

      {!loadingCertificateAudit && !certificateAuditError && expiringSoonCertificates.length > 0 && (
        <DetailsTable
          items={expiringSoonCertificates}
          columns={certificateAuditColumns}
          shouldUsePagination
          itemsPerPage={10}
          hideSelectionDetails
          selectionMode="none"
          style={{ width: "100%" }}
        />
      )}
    </Stack>
  );
};

export default CertificateAuditTab;
